import {IconButton, Menu, MenuItem, Typography} from "@material-ui/core"
import React, {useState, useEffect} from "react"
import styled, {keyframes} from "styled-components"
import MoreVertIcon from "@material-ui/icons/MoreVert"
import LoopIcon from "@material-ui/icons/Loop"
import {useOktaAuth} from "@okta/okta-react"

import {AccountStatus, api} from "@indebted/api"
import {useAgent} from "@indebted/hooks/Agent"
import {useNotification} from "@indebted/components/Notification"
import {ConfirmationDialog} from "@indebted/components/ConfirmationDialog"

interface Account {
	ID: string
	Status?: AccountStatus
}

interface OtherAccountMenuProps {
	account: Account
	reload: () => void
}

function OtherAccountMenu(props: OtherAccountMenuProps) {
	const {account} = props
	const [isReopenAfterRecalledEnabled, setIsReopenAfterRecalledEnabled] = useState(false)
	const oktaObject = useOktaAuth()
	const oktaAuth = oktaObject?.oktaAuth

	useEffect(() => {
		oktaAuth?.getUser().then((user: Record<string, unknown>) => {
			const agentGroups = (user.groups as string[]) || []
			const ALLOWED_GROUP = import.meta.env.VITE_REOPEN_ACCOUNT_ALLOWED_GROUP
			const isAgentAllowed = agentGroups.includes(ALLOWED_GROUP || "")
			setIsReopenAfterRecalledEnabled(isAgentAllowed)
		})
	}, [oktaAuth])

	const map: Partial<Record<AccountStatus, React.FC<OtherAccountMenuProps>>> = {
		NotificationOfAssignmentPending: NotificationOfAssignmentPendingMenu,
		NotificationOfAssignmentPendingDueToEmailScrub: NotificationOfAssignmentPendingDueToEmailScrubMenu,
		NotificationOfAssignmentPermanentlyUndelivered: NotificationOfAssignmentUndeliveredMenu,
		NotificationOfAssignmentTemporarilyUndelivered: NotificationOfAssignmentUndeliveredMenu,
		Recalled: isReopenAfterRecalledEnabled ? RecalledMenu : DisabledMenu,
	}

	const Component = (account.Status ? map[account.Status] : undefined) || DisabledMenu
	return <Component {...props} />
}

function DisabledMenu() {
	return <MoreVertIcon color="disabled" />
}

function RecalledMenu({account, reload}: OtherAccountMenuProps) {
	const [agentEmail] = useAgent()
	const {notification} = useNotification()
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
	const [MenuIcon, setMenuIcon] = useState<JSX.Element>(<MoreVertIcon />)
	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false)
	const [confirmationDialogOnOKFunc, setConfirmationDialogOnOKFunc] = useState<(() => void) | undefined>(undefined)
	const [confirmationDialogDescription, setConfirmationDialogDescription] = useState("")

	const handleClick = (e: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(e.currentTarget)
	}

	const handleMenuClose = () => {
		setAnchorEl(null)
	}

	const handleReopenAfterRecalledClick = () => {
		setMenuIcon(<LoadingIcon />)

		api.account
			.reopenAfterRecalled(account.ID, {
				AgentEmail: agentEmail,
			})
			.then(() => {
				setMenuIcon(<MoreVertIcon />)
				notification.success(
					`Account reopened successfully. Please wait up to 48 hours for propagation in all systems.`,
				)
				setTimeout(reload, 500)
			})
			.catch((error: {Message: string}) => {
				setMenuIcon(<MoreVertIcon />)
				notification.error(error.Message)
			})

		handleMenuClose()
	}

	const confirmDialog = (description: string, onOK: () => void) => {
		handleMenuClose()
		setConfirmationDialogDescription(description)
		setConfirmationDialogOnOKFunc(() => onOK)
		setConfirmationDialogOpen(true)
	}

	return (
		<>
			<ConfirmationDialog
				description={confirmationDialogDescription}
				isOpen={confirmationDialogOpen}
				onCancel={() => {
					setConfirmationDialogOpen(false)
				}}
				onOK={() => {
					confirmationDialogOnOKFunc?.()
					setConfirmationDialogOpen(false)
				}}
			/>

			<IconButton size="small" onClick={handleClick}>
				{MenuIcon}
			</IconButton>
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				getContentAnchorEl={null}
				anchorOrigin={{vertical: "bottom", horizontal: "right"}}
				transformOrigin={{vertical: "top", horizontal: "right"}}
				onClose={handleMenuClose}
			>
				{account.Status === "Recalled" && (
					<MenuItem
						onClick={() =>
							confirmDialog("This action will reopen a recalled account. Do you confirm?", () =>
								handleReopenAfterRecalledClick(),
							)
						}
					>
						Reopen
					</MenuItem>
				)}
			</Menu>
		</>
	)
}

function NotificationOfAssignmentPendingMenu({account, reload}: OtherAccountMenuProps) {
	const [agentEmail] = useAgent()
	const {notification} = useNotification()
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
	const [MenuIcon, setMenuIcon] = useState<JSX.Element>(<MoreVertIcon />)
	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false)
	const [confirmationDialogOnOKFunc, setConfirmationDialogOnOKFunc] = useState<(() => void) | undefined>(undefined)
	const [confirmationDialogDescription, setConfirmationDialogDescription] = useState("")

	const handleClick = (e: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(e.currentTarget)
	}

	const handleMenuClose = () => {
		setAnchorEl(null)
	}

	const handleMenuItemClick = () => {
		setMenuIcon(<LoadingIcon />)

		api.account
			.acknowledgeNotificationOfAssignment(account.ID, {AgentEmail: agentEmail})
			.then(() => {
				setMenuIcon(<MoreVertIcon />)
				notification.success(
					`Notification of Assignment acknowledged successfully. Please wait up to 48 hours for propagation in all systems.`,
				)
				setTimeout(reload, 1000)
			})
			.catch((error: {Message: string}) => {
				setMenuIcon(<MoreVertIcon />)
				notification.error(error.Message)
			})

		handleMenuClose()
	}

	const handleRecallClick = () => {
		setMenuIcon(<LoadingIcon />)

		api.account
			.recall(account.ID, {
				AgentEmail: agentEmail,
			})
			.then(() => {
				setMenuIcon(<MoreVertIcon />)
				notification.success(
					`Account recalled successfully. Please wait up to 48 hours for propagation in all systems.`,
				)
				setTimeout(reload, 500)
			})
			.catch((error: {Message: string}) => {
				setMenuIcon(<MoreVertIcon />)
				notification.error(error.Message)
			})

		handleMenuClose()
	}

	const confirmDialog = (description: string, onOK: () => void) => {
		handleMenuClose()
		setConfirmationDialogDescription(description)
		setConfirmationDialogOnOKFunc(() => onOK)
		setConfirmationDialogOpen(true)
	}

	return (
		<>
			<ConfirmationDialog
				description={confirmationDialogDescription}
				isOpen={confirmationDialogOpen}
				onCancel={() => {
					setConfirmationDialogOpen(false)
				}}
				onOK={() => {
					confirmationDialogOnOKFunc?.()
					setConfirmationDialogOpen(false)
				}}
			/>

			<IconButton size="small" onClick={handleClick}>
				{MenuIcon}
			</IconButton>
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				getContentAnchorEl={null}
				anchorOrigin={{vertical: "bottom", horizontal: "right"}}
				transformOrigin={{vertical: "top", horizontal: "right"}}
				onClose={handleMenuClose}
			>
				<Divider color="textSecondary" variant="caption">
					Notification of assignment
				</Divider>
				<MenuItem
					onClick={() => confirmDialog(`Acknowledge selected. Do you confirm?`, () => handleMenuItemClick())}
				>
					Acknowledge
				</MenuItem>
				<Divider color="textSecondary" variant="caption">
					Recall
				</Divider>
				<MenuItem onClick={() => confirmDialog("Recall selected. Do you confirm?", () => handleRecallClick())}>
					Recall account
				</MenuItem>
			</Menu>
		</>
	)
}

function NotificationOfAssignmentPendingDueToEmailScrubMenu({account, reload}: OtherAccountMenuProps) {
	const [agentEmail] = useAgent()
	const {notification} = useNotification()
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
	const [MenuIcon, setMenuIcon] = useState<JSX.Element>(<MoreVertIcon />)

	const handleClick = (e: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(e.currentTarget)
	}

	const handleClose = () => {
		setAnchorEl(null)
	}

	const handleRecallClick = () => {
		setMenuIcon(<LoadingIcon />)

		api.account
			.recall(account.ID, {
				AgentEmail: agentEmail,
			})
			.then(() => {
				setMenuIcon(<MoreVertIcon />)
				notification.success(
					`Account recalled successfully. Please wait up to 48 hours for propagation in all systems.`,
				)
				setTimeout(reload, 500)
			})
			.catch((error: {Message: string}) => {
				setMenuIcon(<MoreVertIcon />)
				notification.error(error.Message)
			})

		handleClose()
	}

	return (
		<>
			<IconButton size="small" onClick={handleClick}>
				{MenuIcon}
			</IconButton>
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				getContentAnchorEl={null}
				anchorOrigin={{vertical: "bottom", horizontal: "right"}}
				transformOrigin={{vertical: "top", horizontal: "right"}}
				onClose={handleClose}
			>
				<Divider color="textSecondary" variant="caption">
					Recall
				</Divider>
				<MenuItem onClick={handleRecallClick}>Recall account</MenuItem>
			</Menu>
		</>
	)
}

function NotificationOfAssignmentUndeliveredMenu({account, reload}: OtherAccountMenuProps) {
	const [agentEmail] = useAgent()
	const {notification} = useNotification()
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
	const [MenuIcon, setMenuIcon] = useState<JSX.Element>(<MoreVertIcon />)

	const handleClick = (e: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(e.currentTarget)
	}

	const handleClose = () => {
		setAnchorEl(null)
	}

	const handleMenuItemClick = () => {
		setMenuIcon(<LoadingIcon />)

		api.account
			.reTriggerNotificationOfAssignment(account.ID, {AgentEmail: agentEmail})
			.then(() => {
				setMenuIcon(<MoreVertIcon />)
				notification.success(
					`Notification of Assignment re-triggered successfully. Please wait up to 24 hours for the email.`,
				)
				setTimeout(reload, 500)
			})
			.catch((error: {Message: string}) => {
				setMenuIcon(<MoreVertIcon />)
				notification.error(error.Message)
			})

		handleClose()
	}

	const handleRecallClick = () => {
		setMenuIcon(<LoadingIcon />)

		api.account
			.recall(account.ID, {
				AgentEmail: agentEmail,
			})
			.then(() => {
				setMenuIcon(<MoreVertIcon />)
				notification.success(
					`Account recalled successfully. Please wait up to 48 hours for propagation in all systems.`,
				)
				setTimeout(reload, 500)
			})
			.catch((error: {Message: string}) => {
				setMenuIcon(<MoreVertIcon />)
				notification.error(error.Message)
			})

		handleClose()
	}

	return (
		<>
			<IconButton size="small" onClick={handleClick}>
				{MenuIcon}
			</IconButton>
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				getContentAnchorEl={null}
				anchorOrigin={{vertical: "bottom", horizontal: "right"}}
				transformOrigin={{vertical: "top", horizontal: "right"}}
				onClose={handleClose}
			>
				<Divider color="textSecondary" variant="caption">
					Notification of assignment
				</Divider>
				<MenuItem onClick={() => handleMenuItemClick()}>Re-trigger</MenuItem>
				<Divider color="textSecondary" variant="caption">
					Recall
				</Divider>
				<MenuItem onClick={handleRecallClick}>Recall account</MenuItem>
			</Menu>
		</>
	)
}

const loadingIconAnimations = keyframes`
	from { transform:rotate(0deg); }
    to { transform:rotate(-360deg);}
`

const LoadingIcon = styled(LoopIcon)`
	animation: ${loadingIconAnimations} 4s infinite linear;
`

const Divider = styled(Typography)`
	padding: 0 6px;
`

export {OtherAccountMenu}
